import React, { useState } from "react";

import { Role } from "@teamrota/authlib";
import useAuth from "~/src/auth/hooks/use-auth";
import { RotaButton } from "@teamrota/rota-design";

import set from "lodash/fp/set";
import remove from "lodash/fp/remove";
import Button from "~/src/components/button";
import { Column, Row } from "~/src/components/grid";
import LabelValue from "~/src/components/label-value";
import AlertDialog from "~/src/containers/templates/components/dialog";
import MapSearchable from "~/src/components/map-searchable";
import Modal from "~/src/components/modal";
import PageHeader from "~/src/components/page-header";
import CheckInSetupModalContent from "~/src/containers/templates/containers/venue-templates/check-in-setup-modal-content";
import deleteItem from "~/src/graphql/mutations/delete-item/delete-item.decorator";
import getProfile from "~/src/graphql/queries/get-profile/get-profile-query.decorator";
import createOrUpdateVenue from "./graphql/create-update-venue-templates";
import { COLORS, STYLE_CONSTS } from "~/src/styles/config.style";
import { stallPromise } from "~/src/utils";
import asyncConfirm from "~/src/utils/async-confirm";
import { errorModal } from "~/src/utils/errors";
import TemplateItem from "../../components/template-item";
import TemplatePanel from "../../components/template-panel";
import {
  ContentPadding,
  Divider,
  SetUpRowDiv,
  SetUpWrapperDiv,
  StyledGrid,
  StyledRow,
  SubTitle,
  Title,
  TitleContainer,
  StyledTextInput
} from "../../templates.styles";

const DEFAULT_TEMPLATE = {
  name: "",
  lat: 51.505,
  lng: -0.09,
  address: "",
  meetingPoint: "",
  directions: "",
  internalId: ""
};

const VenueTemplates = props => {
  const auth = useAuth();

  const { deleteItem, createOrUpdateVenue, isLoading, user } = props;
  const [activeTemplate, setActiveTemplate] = useState(DEFAULT_TEMPLATE);
  const [shouldShowSavedState, setShouldShowSavedState] = useState(false);
  const [isStateLoading, setIsStateLoading] = useState(false);
  const [nameIsInvalid, setNameIsInvalid] = useState(false);
  const [addressIsInvalid, setAddressIsInvalid] = useState(false);
  const [directionsIsInvalid, setDirectionsIsInvalid] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleSetupModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handlePostSaveIndicator = async () => {
    setShouldShowSavedState(true);
    await Button.successDelay();
    setShouldShowSavedState(false);
  };

  const handleDelete = async venueTemplateId => {
    try {
      if (
        await asyncConfirm("Are you sure you want to delete this template?", {
          confirmButtonText: "Delete"
        })
      ) {
        await deleteItem({
          updateQueries: {
            getProfile: prevData => {
              const prevVenues = prevData?.user?.account?.venues;
              return set(
                "user.account.venues",
                remove({ id: venueTemplateId }, prevVenues)
              )(prevData);
            }
          },
          variables: {
            type: "venues",
            id: venueTemplateId
          }
        });
      }
    } catch (e) {
      errorModal(e);
    }
  };

  const saveVenue = async event => {
    event.preventDefault();

    const {
      name,
      address,
      id,
      meetingPoint,
      directions,
      lat,
      lng,
      internalId
    } = activeTemplate;

    if (!name || !address || !directions) {
      setNameIsInvalid(!name);
      setAddressIsInvalid(!address);
      setDirectionsIsInvalid(!directions);

      return;
    }

    setIsStateLoading(true);

    const variables = {
      id,
      // Make use of GraphQLNonNull by coercing to undefined if string is falsy
      name: name || undefined,
      address: address || undefined,
      meetingPoint,
      directions,
      lat,
      lng,
      internalId: internalId || undefined
    };

    try {
      await stallPromise(createOrUpdateVenue(variables));

      setIsStateLoading(false);
      handlePostSaveIndicator();
    } catch (e) {
      setIsStateLoading(false);
      errorModal(e);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleDeleteClicked = template => {
    handleDelete(template.id);
  };

  const handleEditClicked = template => {
    handleEditMode(template);
  };

  const handleEditMode = venueTemplate => {
    const { name, address } = venueTemplate;
    setActiveTemplate(venueTemplate);
    setNameIsInvalid(!name);
    setAddressIsInvalid(!address);
  };

  const handleChange = key => value => {
    const valueIsObject = typeof value === "object";
    setActiveTemplate({
      ...activeTemplate,
      ...(valueIsObject ? value : { [key]: value })
    });
  };

  const myVenues = user?.account?.venues || [];

  const nameErrorLabel = nameIsInvalid && "Please name this venue";
  const addressErrorLabel = addressIsInvalid && "Please add an address";
  const directionErrorLabel =
    directionsIsInvalid &&
    "Please add directions so that staff can find you easily";

  const hasVenuesCreate = auth.hasRole(Role.VENUES_CREATE);
  const hasVenuesEdit = auth.hasRole(Role.VENUES_EDIT);

  return (
    <div>
      <PageHeader title="Venue Setup" subtext="Create and manage venues" />
      <StyledRow>
        {hasVenuesCreate && (
          <Column medium={1 / 2}>
            <TemplatePanel
              title="Create new Venue"
              onReset={() => setActiveTemplate(DEFAULT_TEMPLATE)}
              isEditMode={Boolean(activeTemplate?.id)}
            >
              <ContentPadding>
                <StyledTextInput
                  isDisabled={false}
                  errorLabel={nameErrorLabel}
                  isRequired
                  label="Template name"
                  placeholder={"Write here"}
                  isBorderless
                  value={activeTemplate?.name}
                  onChangeValue={handleChange("name")}
                />
                <Divider isMarginless />
              </ContentPadding>
              <ContentPadding>
                <MapSearchable
                  value={{ ...activeTemplate }}
                  onChange={handleChange("address")}
                  errorLabel={addressErrorLabel}
                />
                <ContentPadding isVerticalPaddingOnly>
                  <ContentPadding isVerticalPaddingOnly>
                    <LabelValue labelWidth={95} label="Directions">
                      <StyledTextInput
                        isDisabled={false}
                        errorLabel={directionErrorLabel}
                        placeholder={"Write here"}
                        value={activeTemplate?.directions}
                        onChangeValue={handleChange("directions")}
                      />
                    </LabelValue>
                  </ContentPadding>
                  <ContentPadding isVerticalPaddingOnly>
                    <LabelValue
                      labelWidth={95}
                      label="Instructions upon arrival"
                    >
                      <StyledTextInput
                        placeholder="Write here"
                        value={activeTemplate?.meetingPoint}
                        onChangeValue={handleChange("meetingPoint")}
                      />
                    </LabelValue>
                  </ContentPadding>

                  <ContentPadding isVerticalPaddingOnly>
                    <LabelValue labelWidth={95} label="Venue Id">
                      <StyledTextInput
                        placeholder="Write here"
                        value={activeTemplate?.internalId || ""}
                        onChangeValue={handleChange("internalId")}
                      />
                    </LabelValue>
                  </ContentPadding>
                </ContentPadding>
              </ContentPadding>
              <Divider />
              <ContentPadding style={{ textAlign: "right" }}>
                <RotaButton
                  onClick={saveVenue}
                  isSuccess={shouldShowSavedState}
                  isLoading={isStateLoading}
                >
                  {shouldShowSavedState ? "Saved" : "Save"}
                </RotaButton>
              </ContentPadding>
            </TemplatePanel>
          </Column>
        )}
        <Column medium={hasVenuesCreate ? 1 / 2 : 1}>
          <TitleContainer>
            <Title>My Venues</Title>
          </TitleContainer>
          <Row>
            <SetUpWrapperDiv>
              <SubTitle
                fontSize={STYLE_CONSTS.FONT}
                color={COLORS.BLACK}
                fontWeight={STYLE_CONSTS.FONT_WEIGHT_MEDIUM}
              >
                Check in/out & Timesheets:
              </SubTitle>
              <SetUpRowDiv>
                <SubTitle color="#00000" fontSize={STYLE_CONSTS.FONT}>
                  Select and check input method:
                </SubTitle>
                {hasVenuesEdit && (
                  <span>
                    <RotaButton onClick={e => handleSetupModal(e)}>
                      Setup
                    </RotaButton>
                  </span>
                )}
              </SetUpRowDiv>
            </SetUpWrapperDiv>
          </Row>
          <Modal padding="0" isOpen={isModalOpen} isLightOverlay hasBtn>
            <CheckInSetupModalContent
              handleSetupModal={e => handleSetupModal(e)}
              setIsModalOpen={setIsModalOpen}
              isModalOpen={isModalOpen}
            />
          </Modal>
          <Row>
            {!isLoading &&
              myVenues?.map((template, index) => (
                <StyledGrid
                  key={index}
                  gutter="small"
                  xlarge={hasVenuesCreate ? 1 / 2 : 1 / 4}
                >
                  <TemplateItem
                    item={template}
                    title={template?.name}
                    createdAt={template?.createdAt}
                    handleEdit={() => handleEditClicked(template)}
                    handleDelete={() => handleDeleteClicked(template)}
                    canEdit={hasVenuesEdit}
                  >
                    <p>{template?.address}</p>
                  </TemplateItem>
                </StyledGrid>
              ))}
          </Row>
        </Column>
      </StyledRow>
      <AlertDialog
        onHide={handleDialogClose}
        openDialog={isDialogOpen}
        handleClose={handleDialogClose}
        dialogTitle="You do not have the permission to edit/delete this template"
        declineBtnText="OK"
      />
    </div>
  );
};

export default deleteItem(getProfile(createOrUpdateVenue(VenueTemplates)));
